//@flow
import {Typography, makeStyles} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../../../elements/CustomTextField/CustomTextField';
import TextAndBackgroundColorSelector from '../../../../../../elements/TextAndBackgroundColorSelector';
import {colors} from '../../../../../../assets/styles/colors';
import CustomTextEditorInline from '../../../../../../elements/CustomTextEditor/CustomTextEditorInline';
import {useLinkResourceOptions} from '../../../../../store-resources/utils/useLinkResourceOptions';
import {debounce} from '../../../../../../utils/functions';
import {Autocomplete} from '@material-ui/lab';
import {useForm} from 'react-hook-form';
import {HEADER_BANNER_FORM_FIELDS} from './constants';
import {useHeaderBannerActions} from '../../../../utils/header-banner';

import type {HeaderBannerRequest} from '../../../../api/header-banner';
import type {HeaderBanner} from '../../../../../../entities';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  header: {
    paddingBottom: '.5rem',
    borderBottom: `1px solid ${colors.grey300}`,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  subtitle: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  formattedTextInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },
  formattedButton: {
    padding: '.35rem',
    borderRadius: '.25rem',
    border: `1px solid ${colors.grey300}`,
  },
  contentField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.25rem',
  },
  inputSection: {
    display: 'grid',
    gap: '.75rem',
    gridTemplateColumns: '30% 70%',
  },
  submitButton: {
    padding: '.5rem 5rem',
    display: 'block',
    margin: '0 auto',
  },
  autocompleteContainer: {
    position: 'relative',
    width: '100%',
  },
  autocompleteLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    height: '2.5rem',
    padding: '0.25rem',
    width: '100%',
    overflow: 'hidden',
  },
  autocompleteThumbnail: {
    position: 'relative',
    height: '100%',
    borderRadius: '0.25rem',
    aspectRatio: '1/1',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  autocompleteData: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    width: '90%',
  },
  autocompleteLabelName: {
    fontWeight: '500',
    fontSize: '0.75rem',
  },
  autocompleteLabelUrl: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.75rem',
    color: colors.primary,
    maxWidth: '100%',
  },
}));

const textColorOptions = [
  {
    hex: '#FFFFFF',
    name: 'Blanco',
  },
  {
    hex: '#3A3A49',
    name: 'Negro',
  },
];

type Props = {
  onSubmitSuccess: () => void,
  banner?: HeaderBanner,
};

export default function HeaderBannerForm({onSubmitSuccess, banner}: Props) {
  const classes = useStyles();

  const [message, setMessage] = useState(banner?.content || '');

  const {register, setValue, clearErrors, errors, handleSubmit} = useForm({
    defaultValues: {
      [HEADER_BANNER_FORM_FIELDS.BUTTON_TEXT]: banner?.buttonContent,
      [HEADER_BANNER_FORM_FIELDS.BUTTON_URL]: banner?.buttonEntityIdentifier,
      [HEADER_BANNER_FORM_FIELDS.TEXT_COLOR]: banner?.textColor,
      [HEADER_BANNER_FORM_FIELDS.BACKGROUND_COLOR]: banner?.backgroundColor,
    },
  });
  const {createHeaderBanner, editHeaderBanner} = useHeaderBannerActions();

  const {
    categoryOption,
    genericPagesOption,
    loading,
    productOption,
    promotionOption,
    searchProductsByName,
  } = useLinkResourceOptions();

  useEffect(() => {
    register(HEADER_BANNER_FORM_FIELDS.CONTENT, {
      required: 'Campo requerido',
    });

    register(HEADER_BANNER_FORM_FIELDS.BACKGROUND_COLOR);
    register(HEADER_BANNER_FORM_FIELDS.TEXT_COLOR);
    register(HEADER_BANNER_FORM_FIELDS.BUTTON_URL);
  }, [register]);

  const handleInputChange = debounce((value: string) => {
    if (!value.trim() && value.length >= 3) return;

    searchProductsByName(value);
  }, 750);

  useEffect(() => {
    setValue(HEADER_BANNER_FORM_FIELDS.CONTENT, message);
  }, [message, setValue]);

  const onSubmit = data => {
    const isEditing = !!banner;

    const headerBannerRequest: HeaderBannerRequest = {
      backgroundColor: data[HEADER_BANNER_FORM_FIELDS.BACKGROUND_COLOR],
      textColor: data[HEADER_BANNER_FORM_FIELDS.TEXT_COLOR],
      content: data[HEADER_BANNER_FORM_FIELDS.CONTENT],
      enabled: banner ? banner.enabled : false,
      buttonContent: data[HEADER_BANNER_FORM_FIELDS.BUTTON_TEXT],
    };

    const buttonEntityType =
      data[HEADER_BANNER_FORM_FIELDS.BUTTON_URL] && 'URL';

    const buttonEntityIdentifier =
      data[HEADER_BANNER_FORM_FIELDS.BUTTON_URL] || '';

    if (buttonEntityType) {
      headerBannerRequest.buttonEntityType = buttonEntityType;
    }

    if (buttonEntityIdentifier || isEditing) {
      headerBannerRequest.buttonEntityIdentifier = buttonEntityIdentifier;
    }

    if (banner) {
      editHeaderBanner(banner.id, headerBannerRequest).then(() => {
        onSubmitSuccess();
      });
      return;
    }
    createHeaderBanner(headerBannerRequest).then(() => {
      onSubmitSuccess();
    });
  };

  return (
    <form className={classes.root} onSubmit={handleSubmit(onSubmit)}>
      <header className={classes.header}>
        <Typography className={classes.title}>
          Crear Banner de Cabecera
        </Typography>
      </header>
      <div className={classes.content}>
        <div className={classes.contentField}>
          <Typography className={classes.subtitle}>Mensaje de barra</Typography>

          <CustomTextEditorInline
            text={message}
            onChange={value => {
              setMessage(value);
            }}
          />
        </div>

        <div className={classes.contentField}>
          <Typography className={classes.subtitle}>
            Agregar botón (Opcional)
          </Typography>

          <div className={classes.inputSection}>
            <CustomTextField
              placeholder="Nombre de botón"
              name={HEADER_BANNER_FORM_FIELDS.BUTTON_TEXT}
              inputRef={register({
                maxLength: {
                  value: 25,
                  message: 'Máximo 25 caracteres',
                },
              })}
              error={!!errors[HEADER_BANNER_FORM_FIELDS.BUTTON_TEXT]}
              helperText={
                errors[HEADER_BANNER_FORM_FIELDS.BUTTON_TEXT] &&
                errors[HEADER_BANNER_FORM_FIELDS.BUTTON_TEXT].message
              }
            />
            <div className={classes.autocompleteContainer}>
              <Autocomplete
                disablePortal
                options={[
                  ...productOption,
                  ...genericPagesOption,
                  ...categoryOption,
                  ...promotionOption,
                ]}
                fullWidth
                freeSolo
                loading={loading}
                defaultValue={banner?.buttonEntityIdentifier}
                // autoComplete
                filterOptions={(options, state) => {
                  const {inputValue} = state;

                  const resultOptions = options.filter(option =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                  return resultOptions;
                }}
                getOptionLabel={option => {
                  if (typeof option === 'string') return option;
                  return option.url;
                }}
                groupBy={option => option.typeLabel}
                onInput={event => {
                  const value = event.target.value;
                  setValue(HEADER_BANNER_FORM_FIELDS.BUTTON_URL, value);
                  /* if (value.includes('https://') || value.includes('http://')) {
                    return;
                  } */
                  handleInputChange(value);
                }}
                onChange={(event, value) => {
                  if (!value) {
                    setValue(HEADER_BANNER_FORM_FIELDS.BUTTON_URL, '');
                    return;
                  }
                  if (typeof value === 'string') return;

                  setValue(HEADER_BANNER_FORM_FIELDS.BUTTON_URL, value.url);
                  clearErrors(HEADER_BANNER_FORM_FIELDS.BUTTON_URL);
                }}
                renderOption={(option, state) => {
                  const {selected} = state;

                  return (
                    <div
                      className={classes.autocompleteLabel}
                      data-selected={selected}
                    >
                      {option.thumbnail && (
                        <picture className={classes.autocompleteThumbnail}>
                          <img src={option.thumbnail} alt="" />
                        </picture>
                      )}
                      <div className={classes.autocompleteData}>
                        <Typography className={classes.autocompleteLabelName}>
                          {option.name}
                        </Typography>
                        <Typography className={classes.autocompleteLabelUrl}>
                          {option.url}
                        </Typography>
                      </div>
                    </div>
                  );
                }}
                renderInput={params => (
                  <CustomTextField
                    {...params}
                    label=""
                    placeholder="Selecciona una URL de redirección"
                    type="url"
                    fullWidth
                    error={!!errors[HEADER_BANNER_FORM_FIELDS.BUTTON_URL]}
                    helperText={
                      errors[HEADER_BANNER_FORM_FIELDS.BUTTON_URL] &&
                      errors[HEADER_BANNER_FORM_FIELDS.BUTTON_URL].message
                    }
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className={classes.contentField}>
          <TextAndBackgroundColorSelector
            arrayTextColors={textColorOptions}
            onChange={(textColor, bgColor) => {
              setValue(HEADER_BANNER_FORM_FIELDS.TEXT_COLOR, textColor);
              setValue(HEADER_BANNER_FORM_FIELDS.BACKGROUND_COLOR, bgColor);
            }}
            defaultTextColor={banner?.textColor || '#FFFFFF'}
            defaultBackgroundColor={banner?.backgroundColor || '#3A3A49'}
          />
        </div>
      </div>
      <footer className={classes.footer}>
        <CustomButton
          variant="contained"
          type="submit"
          className={classes.submitButton}
        >
          Guardar
        </CustomButton>
      </footer>
    </form>
  );
}
